import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AudioPlayerNew from '../components/AudioPlayerNew';
import { toast } from 'sonner';

const LoadingStages = [
  {
    title: 'Aligning your stars',
    description: 'Calculating your planetary positions from NASA ephemeris data',
    progress: [0, 20],
  },
  {
    title: 'Reading your cosmic blueprint',
    description: 'Analyzing 2,000+ astrological combinations in your chart',
    progress: [20, 60],
  },
  {
    title: 'Translating into your story',
    description: 'AI is weaving your personalized health narrative',
    progress: [60, 90],
  },
  {
    title: 'Creating your audio guide',
    description: 'Sage Varta is preparing to narrate your past',
    progress: [90, 100],
  },
];

const funFacts = [
  "🌟 Your birth chart is as unique as your fingerprint",
  "✨ Ancient sages used palm-leaf manuscripts to record destinies",
  "🔮 Vedic astrology dates back over 5,000 years",
  "🌙 The Moon changes signs every 2.5 days",
  "⭐ Your ascendant changes every 2 hours",
  "🪐 Saturn's transit can last 2.5 years in one sign",
  "💫 There are 27 Nakshatras in Vedic astrology",
  "🌠 Each planet rules specific areas of your life",
  "🎯 Dasha system predicts life's major phases",
  "🕉️ Your Atmakaraka reveals your soul's purpose",
  "🌺 Yoga in your chart indicates special blessings",
  "🔥 Mangal Dosha affects only 8% of all charts",
  "🌸 Jupiter blesses the house it aspects",
  "⚡ Rahu brings sudden changes and opportunities",
  "🌊 Ketu represents spiritual liberation",
];

// Key highlights extracted from predictions - only important story events
const predictionHighlights = {
  hindi: [
    "मजबूत अंतर्ज्ञान",
    "रचनात्मक क्षमता",
    "शनि की चुनौतियाँ",
    "बुद्धि का विकास",
    "गुरु का आशीर्वाद",
    "स्वाभाविक आशावाद",
    "दूसरों का मार्गदर्शन",
    "आध्यात्मिक यात्रा"
  ],
  hinglish: [
    "Strong Intuition",
    "Creative Potential",
    "Saturn ki Challenges",
    "Wisdom Growth",
    "Jupiter ka Blessing",
    "Natural Optimism",
    "Guide Others",
    "Transformation Journey"
  ],
  english: [
    "Strong Intuition",
    "Creative Potential",
    "Saturn's Challenges",
    "Wisdom Shaped",
    "Jupiter's Blessing",
    "Natural Optimism",
    "Guide Others",
    "Life Transformation"
  ]
};

const predictions = {
  hindi: "Aapki janma kundali transformation ka safar dikhati hai. Aapke janam ke samay grahoṇ ki sthiti strong intuition aur creative potential batati hai. Shani ki sthiti early life challenges ko darshati hai jo aapki wisdom ko shape kiya hai. Guru aapko natural optimism aur dusron ko guide karne ki shakti dete hain.",
  hinglish: "Aapki birth chart ek transformation ka journey reveal karti hai. Aapke birth ke time planetary alignment strong intuition aur creative potential suggest karta hai. Saturn ki position early life challenges indicate karta hai jo aapki wisdom ko shape kiya. Jupiter aapko natural optimism aur others ko guide karne ki ability bless karta hai.",
  english: "Your birth chart reveals a journey of transformation. The planetary alignment at your birth suggests strong intuition and creative potential. Saturn's position indicates early life challenges that have shaped your wisdom. Jupiter blesses you with natural optimism and the ability to guide others."
};

const PastStoryUnified = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [currentFact, setCurrentFact] = useState(0);
  const [visibleFacts, setVisibleFacts] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [floatingPhrases, setFloatingPhrases] = useState([]);
  const [assembledPhrases, setAssembledPhrases] = useState([]);
  const predictionId = localStorage.getItem('prediction_id');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
  const audioUrl = `${backendUrl}/api/static/audio/past_${predictionId}.mp3`;
  const audioRef = React.useRef(null);

  // Rotate fun facts during loading
  useEffect(() => {
    if (isLoading) {
      const factInterval = setInterval(() => {
        setCurrentFact((prev) => (prev + 1) % funFacts.length);
        
        const newFactIndex = Math.floor(Math.random() * funFacts.length);
        const position = {
          x: Math.random() * 80 + 10,
          y: Math.random() * 60 + 20,
          rotation: Math.random() * 20 - 10,
          delay: Math.random() * 0.5
        };
        
        setVisibleFacts(prev => [...prev.slice(-4), { fact: funFacts[newFactIndex], position, id: Date.now() }]);
      }, 3000);
      
      return () => clearInterval(factInterval);
    }
  }, [isLoading]);

  // Cinematic animated text particles effect
  useEffect(() => {
    if (audioPlaying) {
      const highlights = predictionHighlights[selectedLanguage];
      let phraseIndex = 0;
      
      // Initial scatter - show all phrases floating
      const initialPhrases = highlights.map((phrase, idx) => ({
        id: `phrase-${idx}`,
        text: phrase,
        x: Math.random() * 70 + 10,
        y: Math.random() * 60 + 10,
        rotation: Math.random() * 30 - 15,
        scale: 0.7 + Math.random() * 0.3,
        delay: idx * 0.2
      }));
      setFloatingPhrases(initialPhrases);
      
      // Assemble phrases one by one in center
      const assembleInterval = setInterval(() => {
        if (phraseIndex < highlights.length) {
          setAssembledPhrases(prev => [...prev, {
            id: `assembled-${phraseIndex}`,
            text: highlights[phraseIndex],
            index: phraseIndex
          }]);
          phraseIndex++;
        } else {
          // Reset and start over
          phraseIndex = 0;
          setAssembledPhrases([]);
        }
      }, 2500);
      
      return () => {
        clearInterval(assembleInterval);
        setFloatingPhrases([]);
        setAssembledPhrases([]);
      };
    } else {
      setFloatingPhrases([]);
      setAssembledPhrases([]);
    }
  }, [audioPlaying, selectedLanguage]);

  useEffect(() => {
    if (!predictionId) {
      toast.error('No prediction ID found');
      navigate('/name-entry');
      return;
    }

    const isDemoId = predictionId.startsWith('demo_');

    if (isDemoId) {
      let currentProgress = 0;
      const demoInterval = setInterval(() => {
        currentProgress += 5;
        setProgress(currentProgress);

        if (currentProgress >= 90) setCurrentStage(3);
        else if (currentProgress >= 60) setCurrentStage(2);
        else if (currentProgress >= 20) setCurrentStage(1);
        else setCurrentStage(0);

        if (currentProgress >= 100) {
          clearInterval(demoInterval);
          setTimeout(() => {
            toast.success('Your story is ready!');
            setIsLoading(false);
          }, 500);
        }
      }, 150);

      return () => clearInterval(demoInterval);
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${backendUrl}/api/story/status/${predictionId}`);
        const data = await response.json();

        setProgress(data.progress || 0);

        if (data.progress >= 90) setCurrentStage(3);
        else if (data.progress >= 60) setCurrentStage(2);
        else if (data.progress >= 20) setCurrentStage(1);
        else setCurrentStage(0);

        if (data.status === 'complete') {
          clearInterval(pollInterval);
          toast.success('Your story is ready!');
          setIsLoading(false);
        } else if (data.status === 'error') {
          clearInterval(pollInterval);
          toast.error('Failed to generate story: ' + (data.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Status poll error:', error);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [predictionId, navigate, backendUrl]);

  const cosmicBackground = {
    minHeight: '100vh',
    height: '100vh',
    background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
    padding: '16px 20px',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  };

  const starsStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(2px 2px at 20% 30%, white, transparent),
      radial-gradient(2px 2px at 60% 70%, white, transparent),
      radial-gradient(1px 1px at 50% 50%, white, transparent),
      radial-gradient(1px 1px at 80% 10%, white, transparent)
    `,
    backgroundSize: '100vw 100vh',
    opacity: 0.5,
    zIndex: 0
  };

  return (
    <div style={cosmicBackground}>
      <div style={starsStyle} />

      <div style={{ maxWidth: '480px', margin: '0 auto', position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <button 
          onClick={() => navigate(-1)}
          style={{
            marginBottom: '16px',
            padding: '6px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'flex-start'
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
        >
          <ChevronLeft style={{ width: '20px', height: '20px', color: '#E8E8E8' }} />
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center' }}
          >
            <h1 style={{
              fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              fontSize: 'clamp(24px, 5vw, 30px)',
              fontWeight: 600,
              color: '#E8E8E8',
              letterSpacing: '1.5px',
              marginBottom: '8px'
            }}>
              YOUR PAST STORY
            </h1>
            <p style={{
              fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              fontSize: '13px',
              color: '#9CA3AF',
              letterSpacing: '0.5px',
              lineHeight: '1.5',
              marginBottom: '12px'
            }}>
              Discover what your stars reveal about your journey
            </p>
            
            {/* Venn Diagram Language Selector - Top Right */}
            <div style={{ 
              position: 'absolute', 
              top: '60px', 
              right: '20px',
              zIndex: 10
            }}>
              <svg width="100" height="60" viewBox="0 0 100 60" style={{ filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))' }}>
                {/* Left Circle - Hindi (H) */}
                <circle
                  cx="32"
                  cy="30"
                  r="22"
                  fill={selectedLanguage === 'hindi' ? 'rgba(237, 193, 97, 0.4)' : 'rgba(255, 255, 255, 0.08)'}
                  stroke={selectedLanguage === 'hindi' ? 'rgba(237, 193, 97, 0.8)' : 'rgba(255, 255, 255, 0.2)'}
                  strokeWidth="2"
                  style={{ 
                    cursor: 'pointer', 
                    transition: 'all 0.3s ease',
                    filter: selectedLanguage === 'hindi' ? 'drop-shadow(0 0 8px rgba(237, 193, 97, 0.5))' : 'none'
                  }}
                  onClick={() => setSelectedLanguage('hindi')}
                />
                
                {/* Right Circle - English (E) */}
                <circle
                  cx="68"
                  cy="30"
                  r="22"
                  fill={selectedLanguage === 'english' ? 'rgba(237, 193, 97, 0.4)' : 'rgba(255, 255, 255, 0.08)'}
                  stroke={selectedLanguage === 'english' ? 'rgba(237, 193, 97, 0.8)' : 'rgba(255, 255, 255, 0.2)'}
                  strokeWidth="2"
                  style={{ 
                    cursor: 'pointer', 
                    transition: 'all 0.3s ease',
                    filter: selectedLanguage === 'english' ? 'drop-shadow(0 0 8px rgba(237, 193, 97, 0.5))' : 'none'
                  }}
                  onClick={() => setSelectedLanguage('english')}
                />
                
                {/* Intersection - Hinglish (clickable area) */}
                <ellipse
                  cx="50"
                  cy="30"
                  rx="14"
                  ry="20"
                  fill={selectedLanguage === 'hinglish' ? 'rgba(237, 193, 97, 0.5)' : 'rgba(255, 255, 255, 0.15)'}
                  stroke={selectedLanguage === 'hinglish' ? 'rgba(237, 193, 97, 0.9)' : 'rgba(255, 255, 255, 0.3)'}
                  strokeWidth="2"
                  style={{ 
                    cursor: 'pointer', 
                    transition: 'all 0.3s ease',
                    filter: selectedLanguage === 'hinglish' ? 'drop-shadow(0 0 10px rgba(237, 193, 97, 0.6))' : 'none'
                  }}
                  onClick={() => setSelectedLanguage('hinglish')}
                />
                
                {/* Text Labels */}
                <text
                  x="32"
                  y="34"
                  textAnchor="middle"
                  fill={selectedLanguage === 'hindi' ? '#edc161' : '#9CA3AF'}
                  fontSize="16"
                  fontWeight="700"
                  fontFamily="Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
                  style={{ 
                    pointerEvents: 'none', 
                    textShadow: selectedLanguage === 'hindi' ? '0 0 6px rgba(237, 193, 97, 0.5)' : 'none'
                  }}
                >
                  H
                </text>
                
                <text
                  x="68"
                  y="34"
                  textAnchor="middle"
                  fill={selectedLanguage === 'english' ? '#edc161' : '#9CA3AF'}
                  fontSize="16"
                  fontWeight="700"
                  fontFamily="Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
                  style={{ 
                    pointerEvents: 'none',
                    textShadow: selectedLanguage === 'english' ? '0 0 6px rgba(237, 193, 97, 0.5)' : 'none'
                  }}
                >
                  E
                </text>
              </svg>
              
              {/* Small label below Venn diagram */}
              <div style={{
                textAlign: 'center',
                marginTop: '4px',
                fontSize: '10px',
                color: '#9CA3AF',
                letterSpacing: '0.5px',
                fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
              }}>
                {selectedLanguage === 'hindi' ? 'हिंदी' : selectedLanguage === 'english' ? 'English' : 'Hinglish'}
              </div>
            </div>
          </motion.div>

          {/* Sage with Cinematic Animated Text Particles */}
          <div style={{ position: 'relative', width: '100%', minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {/* Floating scattered phrases in background */}
            {audioPlaying && floatingPhrases.length > 0 && (
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
                <AnimatePresence>
                  {floatingPhrases.map((phrase) => (
                    <motion.div
                      key={phrase.id}
                      initial={{ 
                        opacity: 0, 
                        scale: 0.5,
                        x: `${phrase.x}%`,
                        y: `${phrase.y}%`,
                        rotate: phrase.rotation
                      }}
                      animate={{ 
                        opacity: [0, 0.2, 0.3, 0.2],
                        scale: phrase.scale,
                        rotate: phrase.rotation + 5,
                        y: [`${phrase.y}%`, `${phrase.y - 5}%`, `${phrase.y}%`]
                      }}
                      exit={{ opacity: 0, scale: 0.3 }}
                      transition={{ 
                        duration: 8,
                        delay: phrase.delay,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      style={{
                        position: 'absolute',
                        fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                        fontSize: 'clamp(12px, 2vw, 14px)',
                        fontWeight: 500,
                        color: 'rgba(237, 193, 97, 0.4)',
                        whiteSpace: 'nowrap',
                        letterSpacing: '0.5px',
                        textShadow: '0 0 10px rgba(237, 193, 97, 0.3)',
                        filter: 'blur(1px)'
                      }}
                    >
                      {phrase.text}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Center: Sage Avatar */}
            <motion.div 
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              style={{ position: 'relative', zIndex: 2 }}
            >
              <img 
                src="/sage-final.png" 
                alt="Sage Varta"
                style={{
                  width: '220px',
                  height: '280px',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  opacity: audioPlaying ? 0.7 : 0.6,
                  filter: audioPlaying 
                    ? 'brightness(0.95) saturate(1.05) drop-shadow(0 0 30px rgba(237, 193, 97, 0.4))' 
                    : 'brightness(0.9) saturate(0.95) drop-shadow(0 0 10px rgba(0, 0, 0, 0.3))',
                  transition: 'all 0.6s ease',
                }}
              />
              
              {/* Speaking Mouth Animation */}
              {audioPlaying && (
                <motion.div
                  style={{
                    position: 'absolute',
                    bottom: '30%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '45px',
                    height: '28px',
                    background: 'radial-gradient(ellipse, rgba(237, 193, 97, 0.4), transparent)',
                    borderRadius: '50%',
                    filter: 'blur(5px)',
                    mixBlendMode: 'screen'
                  }}
                  animate={{
                    scaleY: [1, 1.5, 0.9, 1.4, 1],
                    opacity: [0.3, 0.6, 0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              )}
            </motion.div>

            {/* Assembled Key Phrases - Cinematic Center Display */}
            {audioPlaying && assembledPhrases.length > 0 && (
              <div style={{ 
                position: 'absolute', 
                bottom: '5%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '90%',
                maxWidth: '400px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                alignItems: 'center',
                zIndex: 3
              }}>
                <AnimatePresence mode="popLayout">
                  {assembledPhrases.slice(-3).map((phrase, idx) => (
                    <motion.div
                      key={phrase.id}
                      initial={{ 
                        opacity: 0, 
                        scale: 0.3,
                        y: -50,
                        rotateX: 90
                      }}
                      animate={{ 
                        opacity: idx === assembledPhrases.length - 1 ? 1 : 0.4,
                        scale: idx === assembledPhrases.length - 1 ? 1.1 : 0.85,
                        y: 0,
                        rotateX: 0
                      }}
                      exit={{ 
                        opacity: 0, 
                        scale: 0.5,
                        y: 20
                      }}
                      transition={{ 
                        duration: 0.8,
                        ease: [0.16, 1, 0.3, 1],
                        opacity: { duration: 0.5 }
                      }}
                      style={{
                        fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                        fontSize: idx === assembledPhrases.length - 1 ? 'clamp(16px, 3.5vw, 20px)' : 'clamp(13px, 2.8vw, 15px)',
                        fontWeight: idx === assembledPhrases.length - 1 ? 700 : 500,
                        color: idx === assembledPhrases.length - 1 ? '#E8E8E8' : 'rgba(232, 232, 232, 0.5)',
                        background: idx === assembledPhrases.length - 1 
                          ? 'linear-gradient(135deg, rgba(237, 193, 97, 0.25), rgba(255, 153, 51, 0.2))'
                          : 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(16px)',
                        padding: idx === assembledPhrases.length - 1 ? '14px 28px' : '10px 20px',
                        borderRadius: '20px',
                        border: idx === assembledPhrases.length - 1 
                          ? '2px solid rgba(237, 193, 97, 0.5)' 
                          : '1px solid rgba(237, 193, 97, 0.2)',
                        boxShadow: idx === assembledPhrases.length - 1 
                          ? '0 8px 32px rgba(237, 193, 97, 0.35), inset 0 2px 8px rgba(237, 193, 97, 0.15)'
                          : '0 4px 16px rgba(0, 0, 0, 0.2)',
                        textAlign: 'center',
                        letterSpacing: '0.8px',
                        textShadow: idx === assembledPhrases.length - 1 
                          ? '0 2px 10px rgba(237, 193, 97, 0.4)' 
                          : 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {phrase.text}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>

          {isLoading ? (
            <div style={{ position: 'relative', width: '100%', minHeight: '200px' }}>
              <AnimatePresence>
                {visibleFacts.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.8, x: `${item.position.x}%`, y: `${item.position.y}%` }}
                    animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1, 1, 0.9], rotate: item.position.rotation }}
                    exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ duration: 3, delay: item.position.delay }}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                      fontSize: 'clamp(13px, 2.5vw, 15px)',
                      color: '#E8E8E8',
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      padding: '10px 16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(237, 193, 97, 0.2)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                      maxWidth: '280px',
                      pointerEvents: 'none',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {item.fact}
                  </motion.div>
                ))}
              </AnimatePresence>
              
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: 'center', marginTop: '120px' }}>
                <p style={{
                  fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                  fontSize: '13px',
                  color: '#9CA3AF',
                  letterSpacing: '0.5px'
                }}>
                  {LoadingStages[currentStage].title}...
                </p>
              </motion.div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center', textAlign: 'center' }}
            >
              {audioPlaying ? (
                <motion.p
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    fontSize: '16px',
                    color: '#edc161',
                    letterSpacing: '0.5px',
                    fontWeight: 500
                  }}
                >
                  🎙️ Listening to your past story...
                </motion.p>
              ) : (
                <button
                  onClick={() => {
                    // Try backend audio first
                    if (audioRef.current) {
                      audioRef.current.play().catch(err => {
                        console.log('Audio play error, using text-to-speech:', err);
                        
                        // Use browser text-to-speech with Indian accent
                        const synth = window.speechSynthesis;
                        const utterance = new SpeechSynthesisUtterance(predictions[selectedLanguage]);
                        
                        // Try to get Indian English voice
                        const voices = synth.getVoices();
                        const indianVoice = voices.find(voice => 
                          voice.lang.includes('en-IN') || 
                          voice.name.includes('Indian') ||
                          voice.name.includes('Hindi')
                        );
                        
                        if (indianVoice) {
                          utterance.voice = indianVoice;
                        }
                        
                        utterance.rate = 0.85;
                        utterance.pitch = 1.05;
                        utterance.volume = 1;
                        utterance.lang = selectedLanguage === 'hindi' ? 'hi-IN' : 'en-IN';
                        
                        utterance.onstart = () => setAudioPlaying(true);
                        utterance.onend = () => setAudioPlaying(false);
                        
                        synth.speak(utterance);
                      });
                    } else {
                      // Use text-to-speech with Indian accent
                      const synth = window.speechSynthesis;
                      const utterance = new SpeechSynthesisUtterance(predictions[selectedLanguage]);
                      
                      // Load voices if not loaded yet
                      if (synth.getVoices().length === 0) {
                        synth.addEventListener('voiceschanged', () => {
                          const voices = synth.getVoices();
                          const indianVoice = voices.find(voice => 
                            voice.lang.includes('en-IN') || 
                            voice.name.includes('Indian') ||
                            voice.name.includes('Hindi')
                          );
                          if (indianVoice) utterance.voice = indianVoice;
                        });
                      } else {
                        const voices = synth.getVoices();
                        const indianVoice = voices.find(voice => 
                          voice.lang.includes('en-IN') || 
                          voice.name.includes('Indian') ||
                          voice.name.includes('Hindi')
                        );
                        if (indianVoice) utterance.voice = indianVoice;
                      }
                      
                      utterance.rate = 0.85;
                      utterance.pitch = 1.05;
                      utterance.volume = 1;
                      utterance.lang = selectedLanguage === 'hindi' ? 'hi-IN' : 'en-IN';
                      
                      utterance.onstart = () => setAudioPlaying(true);
                      utterance.onend = () => setAudioPlaying(false);
                      
                      synth.speak(utterance);
                    }
                  }}
                  style={{
                    fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    fontSize: '16px',
                    padding: '14px 32px',
                    background: 'linear-gradient(135deg, rgba(237, 193, 97, 0.2), rgba(255, 215, 0, 0.2))',
                    border: '1px solid rgba(237, 193, 97, 0.4)',
                    borderRadius: '30px',
                    color: '#edc161',
                    cursor: 'pointer',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 16px rgba(237, 193, 97, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, rgba(237, 193, 97, 0.3), rgba(255, 215, 0, 0.3))';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, rgba(237, 193, 97, 0.2), rgba(255, 215, 0, 0.2))';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  ▶ Tap to Listen
                </button>
              )}

              <audio
                ref={audioRef}
                src={audioUrl}
                onPlay={() => setAudioPlaying(true)}
                onPause={() => setAudioPlaying(false)}
                onEnded={() => setAudioPlaying(false)}
              />

              <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
                <button
                  onClick={() => navigate('/login')}
                  style={{
                    fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    fontSize: '14px',
                    color: '#9CA3AF',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    transition: 'all 0.3s ease',
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px'
                  }}
                >
                  Continue →
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PastStoryUnified;